import { useState } from "react";
import { useParams } from "react-router-dom";
import { PatientSurgeryDto } from "../types/patientSurgery";
import { patientSurgeryService } from "../services/patientSurgeryService";
import { useApi } from "./useApi";
import { useMutation } from "./useMutation";

export const usePatientSurgeries = (overridePatientId?: string) => {
  const { patientId: urlPatientId } = useParams<{ patientId: string }>();
  const patientId = overridePatientId ?? urlPatientId;

  // ΕΝΑ ΚΑΙ ΜΟΝΑΔΙΚΟ request: Επιλέγει αυτόματα endpoint ανάλογα με το αν υπάρχει patientId
  const {
    data: surgeries = [],
    loading: isFetching,
    error: getError,
    refetch,
    invalidateCache,
  } = useApi<PatientSurgeryDto[]>(
    patientId ? `patient-surgeries-${patientId}` : "patient-surgeries-all",
    async (signal) => {
      if (patientId) {
        return patientSurgeryService.getByPatientId(patientId, signal);
      }
      return patientSurgeryService.getAll({}, signal);
    },
    [patientId]
  );

  // Mutation για Create (προσθέτει αυτόματα το patientId από το URL στο payload)
  const {
    execute: createExecution,
    loading: isCreating,
    error: createError,
  } = useMutation((newSurgery: any) => {
    const payload = patientId 
      ? { ...newSurgery, patientId: Number(patientId) } 
      : newSurgery;
      
    return patientSurgeryService.create(payload as any);
  });

  // Mutation για Update (με μοναδικό όνομα execution)
  const {
    execute: updateSurgeryExecution,
    loading: isUpdating,
    error: updateError,
  } = useMutation(async ({ id, data }: { id: number; data: any }) => {
    return patientSurgeryService.update(id, data as any);
  });

  // Mutation για Delete
  const {
    execute: deleteExecution,
    loading: isDeleting,
    error: deleteError,
  } = useMutation(async (ids: number[]) => {
    await Promise.all(ids.map((id) => patientSurgeryService.delete(id)));
    return ids;
  });

  const [modalState, setModalState] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "success" as "success" | "error",
  });

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  const createPatientSurgery = async (newSurgery: any): Promise<void> => {
    try {
      await createExecution(newSurgery);
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Το χειρουργείο ασθενούς αποθηκεύτηκε επιτυχώς!",
        type: "success",
      });
      refetch(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα δημιουργίας";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  const updatePatientSurgery = async (id: number, updatedData: any): Promise<void> => {
    try {
      await updateSurgeryExecution({ id, data: updatedData });
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η ενημέρωση του χειρουργείου ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      refetch(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα ενημέρωσης χειρουργείου";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  const deletePatientSurgeries = async (ids: number[]): Promise<void> => {
    try {
      await deleteExecution(ids);
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η διαγραφή ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      refetch(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα κατά τη μαζική διαγραφή";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  return {
    patientSurgeries: surgeries, 
    allSurgeries: surgeries,    
    isLoading: isFetching || isCreating || isDeleting || isUpdating,
    error: getError || createError || deleteError || updateError,
    fetchPatientSurgeries: () => refetch(true),
    fetchSurgeries: () => refetch(true),
    createPatientSurgery,
    updatePatientSurgery,
    deletePatientSurgeries,
    modalState,
    closeModal,
  };
};